/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Divider, Typography } from "@mui/material";
import ContentShell from "components/ContentShell";
import FilePicker from "components/FilePicker";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import ImagePreviewBox from "components/ImagePreviewBox";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createPlayStyle,
  getPlayStyleInfo,
  getPlayStyles,
  updatePlayStyle,
} from "services/playStyleServices";
import { uploadImageV2 } from "services/uploadService";
import useStyles from "styles/banner/bannerEditor";
import { DEFAULT_IMAGE_DIMENSIONS, ImageType } from "utils/imageConstants";

export const PLAY_TYPE_NAMES = [
  "ATTACKING",
  "BALANCED",
  "CREATIVE",
  "DEFENSIVE",
] as const;

const PLAY_STYLE_DIMS = DEFAULT_IMAGE_DIMENSIONS[ImageType.PACK_IMAGE];

interface PlayStyleDetails {
  playType: string;
  imageUrl: string;
  order: string | number;
}

const PlayStyleEditor = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== "create";

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<PlayStyleDetails>({
    playType: "",
    imageUrl: "",
    order: "",
  });
  const [takenNames, setTakenNames] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDims, setImageDims] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>();

  const cancel = () => navigate(configData.routes.playStyles.playStyles);

  const playTypeOptions = useMemo(() => {
    return PLAY_TYPE_NAMES.filter((name) => {
      if (isEdit && name === details.playType) return true;
      return !takenNames.includes(name);
    }).map((name) => ({ id: name, label: name }));
  }, [details.playType, isEdit, takenNames]);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const fetchExistingPlayStyles = async () => {
    try {
      const resp = await getPlayStyles();
      const names = (resp?.data || [])
        .map((item: any) => (item?.playType || "").toUpperCase())
        .filter(Boolean);
      setTakenNames(names);

      if (!isEdit && names.length >= PLAY_TYPE_NAMES.length) {
        showSnackbar(
          "All play styles are already created. Maximum 4 allowed.",
          "error",
        );
        cancel();
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const fetchInfo = async () => {
    setLoading(true);
    try {
      const resp = await getPlayStyleInfo(id);
      if (resp?.data) {
        setDetails({
          playType: (resp.data.playType || "").toUpperCase(),
          imageUrl: resp.data.imageUrl || "",
          order: resp.data.order ?? "",
        });
        setCurrentImageUrl(resp.data.imageUrl || undefined);
      }
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExistingPlayStyles();
    if (isEdit) fetchInfo();
  }, []);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async () => {
    try {
      if (!details.playType) {
        showSnackbar("Play Type is required", "error");
        return;
      }
      if (!PLAY_TYPE_NAMES.includes(details.playType as any)) {
        showSnackbar("Please select a valid play type", "error");
        return;
      }
      const hasOrder =
        details.order !== "" &&
        details.order !== null &&
        details.order !== undefined;

      if (!isEdit && !hasOrder) {
        showSnackbar("Order is required", "error");
        return;
      }
      if (
        hasOrder &&
        (!Number.isInteger(Number(details.order)) || Number(details.order) < 1)
      ) {
        showSnackbar("Order must be a positive number", "error");
        return;
      }
      if (!isEdit && !imageFile) {
        showSnackbar("Image is required", "error");
        return;
      }
      if (isEdit && !imageFile && !currentImageUrl) {
        showSnackbar("Image is required", "error");
        return;
      }

      setLoading(true);

      let uploadUrl: string | undefined;

      if (isEdit) {
        const resp = await updatePlayStyle({
          _id: id,
          ...(hasOrder ? { order: Number(details.order) } : {}),
          ...(imageFile ? { fileType: "image/webp" } : {}),
        });
        uploadUrl = resp?.data?.fileUploadUrl;
      } else {
        const resp = await createPlayStyle({
          playType: details.playType,
          order: Number(details.order),
          fileType: "image/webp",
        });
        uploadUrl = resp?.data?.fileUploadUrl;
      }

      if (imageFile && uploadUrl) {
        const uploadResp = await uploadImageV2({
          data: imageFile,
          uploadUrl,
          resize: true,
          resizeToWidth: imageDims?.targetWidth || PLAY_STYLE_DIMS.width,
          resizeToHeight: imageDims?.targetHeight || PLAY_STYLE_DIMS.height,
        });
        if (!uploadResp?.status) {
          throw new Error(uploadResp?.message || "Image upload failed.");
        }
      }

      showSnackbar(
        isEdit
          ? "Play style updated successfully"
          : "Play style created successfully",
      );
      setLoading(false);
      cancel();
    } catch (error: any) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={isEdit ? "Edit Play Style" : "Create Play Style"}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="button"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="button" variant="contained" onClick={submit}>
            {isEdit ? "Update" : "Save"}
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Play Type"
              name="playType"
              value={details.playType}
              options={playTypeOptions}
              onChange={handleChange}
              disabled={isEdit}
              placeholdertext="Select Play Type"
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Order"
              name="order"
              type="number"
              required={!isEdit}
              inputProps={{ min: 1, step: 1 }}
              onChange={handleChange}
              value={details.order}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Play Style Image
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ width: 200 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
            >
              {previewUrl ? "New image (preview)" : "Current image"}
            </Typography>
            <ImagePreviewBox
              url={previewUrl || currentImageUrl}
              alt={details.playType}
              height={200}
              emptyText="No image yet"
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <FilePicker
              label="Choose image"
              name="play-style-image"
              fileType="image/*"
              showPreview={false}
              imageType={ImageType.PACK_IMAGE}
              enableDimensionControl={true}
              metaData={{
                note: `${isEdit ? "Optional" : "Required"}. Recommended: ${PLAY_STYLE_DIMS.width}×${PLAY_STYLE_DIMS.height}px. Resized to WebP, aspect ratio preserved.`,
              }}
              onFileSelect={(f: any, d?: any) => {
                setImageFile(f || null);
                setImageDims(d ?? null);
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Used in-game for this style of play in the FTU pack selector.
            </Typography>
          </Box>
        </Box>
      </Box>
    </ContentShell>
  );
};

export default PlayStyleEditor;
